import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "@/components/theme-toggle";
import { Link, useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import {
  User, LogOut, Settings, Building2, ChevronDown, Shield, MailWarning, X, Loader2, Menu, Cpu,
} from "lucide-react";
import { ShortListButton } from "@/components/short-list-drawer";
import { BrandPortalStrip } from "@/components/BrandPortalStrip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetClose,
} from "@/components/ui/sheet";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { SiteNavItem } from "@shared/schema";

const FALLBACK_HEADER_NAV = [
  { url: "/",            label: "Home" },
  { url: "/news",        label: "News" },
  { url: "/products",    label: "Products" },
  { url: "/companies",   label: "Companies" },
  { url: "/people",      label: "People" },
  { url: "/magazine",    label: "Magazine" },
  { url: "/videos",      label: "Videos" },
  { url: "/membership",  label: "Membership" },
  { url: "/about",       label: "About" },
];

const OUR_BRANDS = [
  {
    name: "Sustainable Engineering Alliance",
    slug: "sustainable-engineering-alliance",
    desc: "Sustainability, lifecycle & circular design",
    color: "bg-emerald-500",
  },
  {
    name: "Disti",
    slug: "disti",
    desc: "Electronics, electrical & PCB design",
    color: "bg-blue-500",
  },
  {
    name: "Devkit",
    slug: "devkit",
    desc: "Development kits, prototyping & embedded systems",
    color: "bg-violet-500",
  },
];

export function SiteHeader() {
  const { user, isAuthenticated, isLoading, logout } = useAuth();
  const [location, setLocation] = useLocation();
  const { toast } = useToast();
  const [verifyBannerDismissed, setVerifyBannerDismissed] = useState(false);
  const [stripCollapsed, setStripCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const lastScrollY = useRef(0);

  // Close mobile menu on navigation
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  // Scroll-aware auto-collapse for the portal strip
  useEffect(() => {
    const handleScroll = () => {
      const currentY = window.scrollY;
      if (currentY > 80 && currentY > lastScrollY.current) {
        setStripCollapsed(true);
      } else if (currentY < lastScrollY.current - 20) {
        setStripCollapsed(false);
      }
      lastScrollY.current = currentY;
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Sync dismissal state with localStorage, scoped per user so it survives navigation/refresh
  useEffect(() => {
    if (!user?.id) return;
    const key = `verify-banner-dismissed-${user.id}`;
    if (localStorage.getItem(key) === "true") {
      setVerifyBannerDismissed(true);
    }
  }, [user?.id]);

  const dismissVerifyBanner = () => {
    if (user?.id) {
      localStorage.setItem(`verify-banner-dismissed-${user.id}`, "true");
    }
    setVerifyBannerDismissed(true);
  };

  const { data: siteNavItems = [] } = useQuery<SiteNavItem[]>({
    queryKey: ["/api/site-nav"],
    staleTime: 60_000,
  });

  const { data: myAffiliations = [] } = useQuery<{ affiliationRole: string; affiliationStatus: string }[]>({
    queryKey: ["/api/affiliations/my"],
    enabled: isAuthenticated,
    staleTime: 60_000,
  });
  const isSupplier = myAffiliations.some(
    (a) => a.affiliationStatus === "approved" && (a.affiliationRole === "contributor" || a.affiliationRole === "admin")
  );

  const { data: siteBranding } = useQuery<{ logoUrl: string; mobileLogoUrl?: string; siteName: string }>({
    queryKey: ["/api/public/site-branding"],
    staleTime: 5 * 60_000,
  });

  const logoSrc = siteBranding?.logoUrl || null;
  const mobileLogoSrc = siteBranding?.mobileLogoUrl || null;

  const headerLinks = siteNavItems.filter(i => i.placement === "header").length > 0
    ? siteNavItems.filter(i => i.placement === "header").sort((a, b) => a.displayOrder - b.displayOrder)
    : FALLBACK_HEADER_NAV.map((l, idx) => ({ ...l, id: l.url, placement: "header" as const, displayOrder: idx, isVisible: true, openInNewTab: false, createdAt: null, updatedAt: null }));

  const resendMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/auth/resend-verification"),
    onSuccess: () => toast({ title: "Verification email sent — check your inbox" }),
    onError: (err: any) => toast({ title: err?.message || "Failed to send email", variant: "destructive" }),
  });

  const isAdminRole = user?.role === "admin" || user?.role === "super_admin";
  const showVerifyBanner = !verifyBannerDismissed && isAuthenticated && user && !user.emailVerified && !isAdminRole;

  const getInitials = () => {
    if (!user) return "U";
    const first = user.firstName?.[0] || "";
    const last = user.lastName?.[0] || "";
    return (first + last).toUpperCase() || user.email?.[0]?.toUpperCase() || "U";
  };

  const handleLogout = () => {
    setMobileMenuOpen(false);
    logout();
  };

  const navigate = (href: string) => {
    setMobileMenuOpen(false);
    setLocation(href);
  };

  return (
    <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">

      {/* ── Brand Portal Strip ── */}
      <BrandPortalStrip
        isCollapsed={stripCollapsed}
        onCollapse={() => setStripCollapsed(true)}
      />

      {/* ── Email verification banner ── */}
      {showVerifyBanner && (
        <div className="bg-amber-50 dark:bg-amber-950/60 border-b border-amber-200 dark:border-amber-800 px-4 py-2">
          <div className="mx-auto max-w-7xl flex items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-sm text-amber-800 dark:text-amber-200">
              <MailWarning className="h-4 w-4 shrink-0" />
              <span>Please verify your email address to complete your account setup.</span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs border-amber-400 text-amber-800 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900"
                onClick={() => resendMutation.mutate()}
                disabled={resendMutation.isPending}
                data-testid="button-resend-verification-banner"
              >
                {resendMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : "Resend email"}
              </Button>
              <button
                onClick={dismissVerifyBanner}
                className="text-amber-600 dark:text-amber-400 hover:text-amber-900 dark:hover:text-amber-100"
                aria-label="Dismiss"
                data-testid="button-dismiss-verify-banner"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Main header row ── */}
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4">
        <Link href="/" className="flex items-center gap-3 shrink-0" data-testid="link-logo" aria-label="Go to homepage">
          {/* Mobile logo — shown below lg */}
          {(mobileLogoSrc || logoSrc) ? (
            <img
              src={mobileLogoSrc || logoSrc!}
              alt={siteBranding?.siteName || "FM World"}
              width={48}
              height={48}
              className={`object-contain block lg:hidden ${mobileLogoSrc ? "h-16 w-16" : "h-8 w-auto max-w-[120px]"}`}
              data-testid="img-logo-mobile"
            />
          ) : (
            <span className="font-bold text-base block lg:hidden" data-testid="img-logo-mobile">
              {siteBranding?.siteName || "FM World"}
            </span>
          )}
          {/* Desktop logo — shown lg+ */}
          {logoSrc ? (
            <img
              src={logoSrc}
              alt={siteBranding?.siteName || "FM World"}
              width={160}
              height={40}
              className="h-10 w-auto object-contain hidden lg:block"
              data-testid="img-logo"
            />
          ) : (
            <span className="font-bold text-lg hidden lg:block" data-testid="img-logo">
              {siteBranding?.siteName || "FM World"}
            </span>
          )}
        </Link>

        {/* Desktop nav — hidden below lg */}
        <nav className="hidden lg:flex items-center gap-6">
          {headerLinks.map((item, idx) => (
            item.openInNewTab ? (
              <a
                key={item.id ?? idx}
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {item.label}
              </a>
            ) : (
              <Link
                key={item.id ?? idx}
                href={item.url}
                className={`text-sm transition-colors ${
                  (item.url === "/" ? location === "/" : location.startsWith(item.url))
                    ? "font-semibold text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {item.label}
              </Link>
            )
          ))}

          {/* Our Brands dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                data-testid="nav-our-brands"
              >
                Our Brands
                <ChevronDown className="h-3.5 w-3.5 transition-transform [[data-state=open]_&]:rotate-180" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-72 p-0" data-testid="dropdown-our-brands">
              <div className="px-4 py-3 bg-muted/50 border-b">
                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  Our Brand Family
                </p>
                <p className="text-xs text-muted-foreground/70 mt-0.5">
                  Specialists across the whole BoM
                </p>
              </div>
              <div className="py-1">
                {OUR_BRANDS.map(b => (
                  <DropdownMenuItem key={b.slug} asChild data-testid={`dropdown-brand-${b.slug}`}>
                    <Link href={`/brand/${b.slug}`} className="flex items-center gap-3 px-4 py-2.5 cursor-pointer">
                      <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${b.color}`} />
                      <div>
                        <div className="text-sm font-medium text-foreground">{b.name}</div>
                        <div className="text-xs text-muted-foreground">{b.desc}</div>
                      </div>
                    </Link>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link href="/brand/future-manufacturing" className="flex items-center px-4 py-2 text-xs text-muted-foreground hover:text-foreground cursor-pointer">
                    View all brands →
                  </Link>
                </DropdownMenuItem>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>

        {/* Action bar (right side) */}
        <div className="flex items-center gap-2">
          {/* Dev portal button — desktop only */}
          {import.meta.env.DEV && (
            <Button variant="outline" size="sm" asChild className="hidden lg:flex gap-1.5 border-dashed border-amber-500/50 text-amber-600 dark:text-amber-400" data-testid="button-dev-admin">
              <Link href="/staff/portal">
                <Shield className="h-3.5 w-3.5" />
                <span>Staff Portal</span>
              </Link>
            </Button>
          )}
          <ShortListButton />
          {/* Theme toggle — desktop only; available in mobile drawer */}
          <span className="hidden lg:block">
            <ThemeToggle />
          </span>

          {/* Desktop auth — hidden below lg */}
          <div className="hidden lg:block">
            {isLoading ? (
              <div className="h-9 w-20 animate-pulse bg-muted rounded-md" />
            ) : isAuthenticated && user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="gap-2" data-testid="button-user-menu">
                    <Avatar className="h-7 w-7">
                      <AvatarImage src={user.profileImageUrl || undefined} alt={user.firstName || "User"} />
                      <AvatarFallback className="text-xs">{getInitials()}</AvatarFallback>
                    </Avatar>
                    <span className="hidden sm:inline text-sm">
                      {user.firstName || user.email?.split("@")[0]}
                    </span>
                    <ChevronDown className="h-4 w-4 opacity-50" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user.firstName} {user.lastName}</p>
                    <p className="text-xs text-muted-foreground">{user.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setLocation("/account")} data-testid="menu-item-profile">
                    <User className="mr-2 h-4 w-4" />
                    My Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setLocation("/account/affiliations")} data-testid="menu-item-affiliations">
                    <Building2 className="mr-2 h-4 w-4" />
                    Company Affiliations
                  </DropdownMenuItem>
                  {isSupplier && (
                    <DropdownMenuItem onClick={() => setLocation("/supplier/radars")} data-testid="menu-item-supplier-portal">
                      <Cpu className="mr-2 h-4 w-4" />
                      Supplier Portal
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => setLocation("/account/settings")} data-testid="menu-item-settings">
                    <Settings className="mr-2 h-4 w-4" />
                    Account Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="menu-item-logout">
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2">
                <Button variant="ghost" asChild data-testid="button-login">
                  <Link href="/login">Sign In</Link>
                </Button>
                <Button variant="outline" asChild data-testid="button-register">
                  <Link href="/register">Register</Link>
                </Button>
              </div>
            )}
          </div>

          {/* Hamburger — visible only below lg */}
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setMobileMenuOpen(true)}
            aria-label="Open navigation menu"
            data-testid="button-mobile-menu"
          >
            <Menu className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {/* ── Mobile navigation Sheet ── */}
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent side="left" className="w-72 sm:w-80 p-0 flex flex-col overflow-y-auto">
          <SheetHeader className="px-4 py-4 border-b flex-row items-center justify-between shrink-0">
            <SheetTitle className="text-base font-semibold">Navigation</SheetTitle>
            <SheetClose asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 -mr-1" data-testid="button-mobile-menu-close">
                <X className="h-4 w-4" />
              </Button>
            </SheetClose>
          </SheetHeader>

          <div className="flex-1 py-2">
            {/* Main nav links */}
            <nav className="px-2" aria-label="Mobile navigation">
              {headerLinks.map((item, idx) => (
                item.openInNewTab ? (
                  <a
                    key={item.id ?? idx}
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center px-3 py-2.5 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.label}
                  </a>
                ) : (
                  <Link
                    key={item.id ?? idx}
                    href={item.url}
                    className={`flex items-center px-3 py-2.5 rounded-md text-sm transition-colors ${
                      (item.url === "/" ? location === "/" : location.startsWith(item.url))
                        ? "font-semibold text-foreground bg-muted"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted"
                    }`}
                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                )
              ))}
            </nav>

            <Separator className="my-3" />

            {/* Our Brands section */}
            <div className="px-2">
              <p className="px-3 pb-1.5 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                Our Brands
              </p>
              {OUR_BRANDS.map(b => (
                <Link
                  key={b.slug}
                  href={`/brand/${b.slug}`}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-muted transition-colors"
                  data-testid={`mobile-brand-${b.slug}`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${b.color}`} />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-foreground leading-tight">{b.name}</div>
                    <div className="text-xs text-muted-foreground leading-tight">{b.desc}</div>
                  </div>
                </Link>
              ))}
              <Link
                href="/brand/future-manufacturing"
                className="flex items-center px-3 py-2 rounded-md text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                View all brands →
              </Link>
            </div>

            {/* No mobile brand strip re-expand — brand network dropdown handles it */}

            <Separator className="my-3" />

            {/* Auth section */}
            <div className="px-2">
              {isLoading ? (
                <div className="h-10 mx-3 animate-pulse bg-muted rounded-md" />
              ) : isAuthenticated && user ? (
                <>
                  <div className="px-3 py-2 mb-1">
                    <div className="flex items-center gap-2.5">
                      <Avatar className="h-8 w-8 shrink-0">
                        <AvatarImage src={user.profileImageUrl || undefined} alt={user.firstName || "User"} />
                        <AvatarFallback className="text-xs">{getInitials()}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{user.firstName} {user.lastName}</p>
                        <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => navigate("/account")}
                    className="flex w-full items-center gap-2 px-3 py-2.5 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                    data-testid="mobile-menu-item-profile"
                  >
                    <User className="h-4 w-4 shrink-0" />
                    My Profile
                  </button>
                  <button
                    onClick={() => navigate("/account/affiliations")}
                    className="flex w-full items-center gap-2 px-3 py-2.5 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                    data-testid="mobile-menu-item-affiliations"
                  >
                    <Building2 className="h-4 w-4 shrink-0" />
                    Company Affiliations
                  </button>
                  {isSupplier && (
                    <button
                      onClick={() => navigate("/supplier/radars")}
                      className="flex w-full items-center gap-2 px-3 py-2.5 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                      data-testid="mobile-menu-item-supplier-portal"
                    >
                      <Cpu className="h-4 w-4 shrink-0" />
                      Supplier Portal
                    </button>
                  )}
                  <button
                    onClick={() => navigate("/account/settings")}
                    className="flex w-full items-center gap-2 px-3 py-2.5 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                    data-testid="mobile-menu-item-settings"
                  >
                    <Settings className="h-4 w-4 shrink-0" />
                    Account Settings
                  </button>
                  <button
                    onClick={handleLogout}
                    className="flex w-full items-center gap-2 px-3 py-2.5 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                    data-testid="mobile-menu-item-logout"
                  >
                    <LogOut className="h-4 w-4 shrink-0" />
                    Sign Out
                  </button>
                </>
              ) : (
                <div className="px-3 flex flex-col gap-2">
                  <Button asChild className="w-full" data-testid="mobile-button-login">
                    <Link href="/login" onClick={() => setMobileMenuOpen(false)}>Sign In</Link>
                  </Button>
                  <Button variant="outline" asChild className="w-full" data-testid="mobile-button-register">
                    <Link href="/register" onClick={() => setMobileMenuOpen(false)}>Register</Link>
                  </Button>
                </div>
              )}
            </div>

            <Separator className="my-3" />

            {/* Utility row — theme toggle + dev portal */}
            <div className="px-5 pb-4 flex items-center justify-between">
              <ThemeToggle />
              {import.meta.env.DEV && (
                <Link
                  href="/staff/portal"
                  className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400 hover:underline"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="mobile-link-staff-portal"
                >
                  <Shield className="h-3.5 w-3.5" />
                  Staff Portal
                </Link>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </header>
  );
}
